"use client";
import React from "react";
import { MessageSquare, Star } from "lucide-react";
import ReviewCard, { ReviewProps } from "components/common/Card/Review";
import Pagination from "components/common/Pagination";
import ReviewService from "services/review";
import { REVIEW_PAGE_SIZE, buildReviewQuery } from "services/review/constant";
import { get } from "utils/lodash";
import usePagination from "hooks/usePagination";

interface ReviewSectionProps {
  shopId: string;
  /** Trang 1 lấy sẵn ở server (page.tsx) — có thì không gọi lại khi mount */
  initialData?: unknown[];
  initialTotal?: number;
  averageRating?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
}

const ReviewSection = ({
  shopId,
  initialData,
  initialTotal = 0,
  averageRating = 0,
  onLoadMore,
  hasMore = false,
  loading = false,
}: ReviewSectionProps) => {
  const pagination = usePagination({
    initialParams: {
      page: 1,
      limit: REVIEW_PAGE_SIZE,
      initialItems: initialData,
      initialTotal,
    },
    loader: async (payload) => {
      const query = buildReviewQuery({
        shopId,
        page: get(payload, "page", 1),
        limit: get(payload, "limit", 10),
      });
      const res = await ReviewService.find(query);
      const data = get(res, "data", []);
      const total = get(res, "params.total", 0);
      return {
        data,
        total,
      };
    },
  });

  React.useEffect(() => {
    if (!initialData) pagination?.onReload?.();
  }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalReviews = get(pagination, "total", 0);
  return (
    <div className="max-md:py-4">
      {/* Header (mobile gọn 1 dòng) */}
      <div className="md:hidden mb-3 flex items-end justify-between">
        <h2 className="text-[17px] font-extrabold text-black01 !mb-0">Đánh giá</h2>
        <span className="text-xs text-gray-500">{totalReviews} đánh giá</span>
      </div>
      {/* Header (desktop) */}
      <div className="hidden md:block mb-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Đánh giá</h2>
        </div>

        <div className="flex items-center gap-4">
          <p className="text-gray-600">{totalReviews} đánh giá</p>
          {averageRating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-gray-700 font-medium">
                {averageRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Reviews Grid */}
      {pagination?.items?.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {pagination?.items?.map((review: unknown, index: number) => (
              <ReviewCard
                key={get(review, "id", index)}
                {...(review as ReviewProps)}
                className="w-full"
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={onLoadMore}
                disabled={loading}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Đang tải..." : "Xem thêm đánh giá"}
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Empty State - gọn, không để khoảng trống lớn giữa trang */
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-4">
          <MessageSquare className="w-6 h-6 text-gray-300 flex-shrink-0" />
          <p className="text-sm text-gray-500 !mb-0">
            Cửa hàng này chưa có đánh giá. Hãy là người đầu tiên trải nghiệm và
            chia sẻ cảm nhận nhé!
          </p>
        </div>
      )}
      <Pagination pagination={pagination} />
    </div>
  );
};

export default ReviewSection;
